<template>
    <div>
        <template v-if="files.length || searchLoading">
            <div v-if="!hideTitle" class="d-sm-flex flex-sm-row align-items-center mb-2 mt-4" :class="titleClass">
                <h3 class="h4 d-flex align-items-center mb-0 mr-3">
                    <span class="mr-2">{{ getTitle() }}</span>
                    <template v-if="customFilesCount">
                        <small v-if="filesCount !== null">({{ filesCount }})</small>
                    </template>
                    <small v-else>({{ files.length }})</small>
                </h3>
                <div v-if="selectable" class="d-flex align-items-center mt-2 mt-sm-0">
                    <button type="button" class="btn btn-primary btn-sm" @click="selectAllButton">
                        <span class="d-flex align-items-center">
                            <CheckIcon size="14" class="text-white mr-1"/>
                            {{ $t('Select all') }}
                        </span>
                    </button>
                    <EBtnLoader v-if="checkedFiles.length" :is-submit-loading="downloadLoading" btn-class="btn-sm" class="ml-2" @clicked="downloadSelected">
                        <span class="d-flex align-items-center">
                            <DownloadIcon size="14" class="text-white mr-1"/>
                            {{ $t('Download selected') }} ({{checkedFiles.length}})
                        </span>
                    </EBtnLoader>
                </div>
            </div>
            <template>
                <slot/>
            </template>
            <div v-if="!searchLoading" class="d-flex flex-wrap mx-n1">
                <div v-for="file in visibleFiles" :key="file.id" class="px-1 mb-2 file-area" @mouseleave="displayCheckbox = null" @mouseover="displayCheckbox = file.id">
                    <ECheckbox
                        v-if="selectable"
                        :id="`file-${file.id}`"
                        v-model="checkedFiles"
                        success
                        class="d-none position-absolute ml-1 mt-1"
                        :class="{'d-inline' : displayCheckbox === file.id || checkedFiles.length}"
                        :val="file.id"/>
                    <div @click="openModal(file)">
                        <FileContainer :file="file" :type="type"/>
                    </div>
                </div>
                <div v-for="file in invisibleFiles" :key="file.id" class="px-1 mb-2 file-area e3-file-placeholder">
                    <div class="d-flex justify-content-center align-items-center placeholder-image">
                        <ImageIcon size="56" class="text-secondary"/>
                    </div>
                </div>
            </div>
            <ELoader v-else class="p-0"/>
        </template>
        <EmptyListMessage v-else-if="searching" class="border bg-white rounded p-2 mt-3">
            {{ $t('No documents found') }}
        </EmptyListMessage>
        <ViewGalleryFileModal v-if="showGalleryFileModal"/>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import FileContainer from '@views/folder/components/FileContainer.vue';
import ViewGalleryFileModal from '@views/gallery/modals/ViewFileModal.vue';
import JSZip from 'jszip';
import httpService from '@common/services/http.service';
import {download} from '@services/helper.service';
import EventBus from '@/utils/eventBus';
import {mapGetters} from 'vuex';
import CheckIcon from '@components/svg-icons/CheckIcon.vue';
import DownloadIcon from '@components/svg-icons/DownloadIcon.vue';
import ImageIcon from '@components/svg-icons/ImageIcon.vue';

export default {
    props: {
        files: Array,
        filesCount: Number,
        customFilesCount: Boolean,
        type: String,
        searchLoading: Boolean,
        searching: Boolean,
        zipName: String,
        paginated: {
            type: Boolean,
            default: false
        },
        hideTitle: {
            type: Boolean,
            default: false
        },
        titleClass: {
            type: String,
            default: ''
        },
        selectable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            offsetTop: 0,
            shownFiles: 25,
            checkedFiles: [],
            displayCheckbox: null,
            allSelected: false,
            downloadLoading: false
        };
    },
    computed: {
        ...mapGetters(['folder']),
        visibleFiles() {
            if (this.allSelected) {
                this.selectAll(true);
            }
            if (this.paginated) {
                return this.files;
            } else {
                return this.files.slice(0, this.shownFiles);
            }
        },
        invisibleFiles() {
            if (this.paginated) {
                return [];
            } else {
                return this.files.slice(this.shownFiles, this.files.length);
            }
        },
        fileCount() {
            return this.files.length;
        },
        showGalleryFileModal() {
            return ['gallery', 'diary', 'child', 'gallery_builtin_child', 'gallery_builtin_diary'].includes(this.type);
        }
    },
    watch: {
        offsetTop () {
            if (!this.paginated && this.shownFiles < this.fileCount) {
                this.scrollCallBack();
            }
        }
    },
    methods: {
        getTitle() {return this.type === 'document' ? this.$t('Files') : this.$t('Images and videos');},
        selectAllButton() {
            this.allSelected = !this.allSelected;
            this.selectAll(this.allSelected);
        },
        selectAll(value) {
            if (value) {
                this.checkedFiles = this.files.map(f => f.id);
            } else {
                this.checkedFiles = [];
            }
        },
        openModal(file) {
            const args = {
                file: file,
                type: this.type,
                ...(['child', 'gallery_builtin_child', 'gallery_builtin_diary'].includes(this.type) && { files: this.files })
            };
            EventBus.emit('show-view-media-file-modal', args);
        },
        isElementInWindow(element) {
            const item = element.getBoundingClientRect();

            return (
                item.left >= 0 &&
                item.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                item.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        },
        scrollCallBack() {
            if (!this.paginated) {
                const placeholders = document.querySelectorAll('.placeholder-image');

                for (let i = 0; i < placeholders.length; i++) {
                    if (this.isElementInWindow(placeholders[i]) && this.shownFiles < this.fileCount) {
                        this.shownFiles++;
                    }
                }
            }
        },
        async downloadSelected() {
            this.downloadLoading = true;
            const zip = new JSZip();

            let files = [];
            const zipName = this.zipName ?? this.getTitle();

            if (this.checkedFiles.length) {
                files = this.visibleFiles.filter(f => this.checkedFiles.includes(f.id));
            }

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const name = file.name + (file.filename ? file.filename.substr(file.filename.indexOf('.')) : '');

                if (file.url) {
                    await httpService.download(file.url, {withCredentials: false}).then(res => {
                        zip.file(name, res.data);
                    });
                }
            }

            zip.generateAsync({type: 'blob'}).then(function (content) {
                download(content, {name: zipName, filename: zipName+'.zip'});
            }).finally(() => {
                this.downloadLoading = false;
            });
        },
        handleScroll: debounce(function() {
            this.offsetTop = window.pageYOffset || document.documentElement.scrollTop;
        }, 300)
    },
    components: {
        FileContainer,
        ViewGalleryFileModal,
        CheckIcon,
        DownloadIcon,
        ImageIcon
    },
    created() {
        document.addEventListener('scroll', this.handleScroll);
    },
    mounted() {
        this.scrollCallBack();
    },
    beforeDestroy(){  // Vue 3 Rename beforeUnmount
        document.removeEventListener('scroll', this.handleScroll);
    }
};
</script>

<style lang="scss" scoped>
@import "@common/styles/variables/_variables.scss";

.e3-file-placeholder {
    position: relative;
    width: 100%;
    &::before {
        content: '';
        display: block;
        padding-top: 100%;
    }

    .placeholder-image {
        position: absolute;
        top: 0%;
        bottom: 0%;
        left: 0.25rem;
        right: 0.25rem;
        height: 100%;
        border-radius: 0.33rem;
        background-color: $gray-200;
    }
}
</style>
